import {
  type NullableSize,
  createElementSize,
} from "@solid-primitives/resize-observer";
import { createEffect, createSignal, onMount } from "solid-js";

import { onPlayerLoad, usePlayer } from "~/components/PlayerContext";
import styles from "~/components/player/Effect.module.scss";
import {
  AudioAnalyser,
  type AudioAnalysis,
} from "~/components/player/misc/AudioAnalyser";

export default function Effect() {
  const [outerRef, setOuterRef] = createSignal<HTMLDivElement>();
  const [canvasRef, setCanvasRef] = createSignal<HTMLCanvasElement>();
  const player = usePlayer();

  const outerSize = createElementSize(outerRef);

  const fixCanvas = (rectangle: Readonly<NullableSize>) => {
    const canvas = canvasRef();
    if (
      !canvas ||
      getComputedStyle(canvas).display === "none" ||
      rectangle.width === null
    ) {
      return;
    }

    const canvasContext = canvas.getContext("2d");
    canvas.style.width = canvas.style.height = "";

    if (canvas.getClientRects().length === 0) {
      return;
    }

    const pixelRatio = window.devicePixelRatio;
    canvas.style.width = `${Math.floor(rectangle.width).toString()}px`;
    canvas.style.height = `${Math.floor(rectangle.height).toString()}px`;
    canvas.width = Math.floor(rectangle.width) * pixelRatio;
    canvas.height = Math.floor(rectangle.height) * pixelRatio;
    canvasContext?.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  };

  const stopAudioAnalysis = () => {
    const canvas = canvasRef();
    if (!canvas) {
      return;
    }

    const pixelRatio = window.devicePixelRatio;
    const canvasContext = canvas.getContext("2d");
    const width = canvas.width / pixelRatio;
    const height = canvas.height / pixelRatio;
    canvasContext?.clearRect(0, 0, width, height);
  };

  const analyseAudio = (event?: AudioAnalysis) => {
    const canvas = canvasRef();
    if (!event || !canvas) {
      stopAudioAnalysis();
      return;
    }

    const frequencyData = event.frequencyMerged;

    const pixelRatio = window.devicePixelRatio;

    const canvasContext = canvas.getContext("2d");

    if (!canvasContext) {
      return;
    }

    const width = canvas.width / pixelRatio;
    const height = canvas.height / pixelRatio;

    const slicesNumber = Math.round(width / 10);
    const sliceWidth = width / slicesNumber;
    const sliceValues = Array.from<number>({ length: slicesNumber });
    const sliceDivisors = Array.from<number>({ length: slicesNumber });
    const frequenciesNumber = frequencyData.length / 2;

    for (let index = 0; index < frequenciesNumber; index++) {
      const sliceIndex = Math.floor((index * slicesNumber) / frequenciesNumber);
      sliceValues[sliceIndex] =
        (sliceValues[sliceIndex] ?? 0) + frequencyData[index];
      sliceDivisors[sliceIndex] = (sliceDivisors[sliceIndex] ?? 0) + 1;
    }

    canvasContext.clearRect(0, 0, width, height);
    canvasContext.fillStyle = getComputedStyle(canvas).color;

    canvasContext.strokeStyle = getComputedStyle(canvas).borderColor;

    for (const [index, value] of sliceValues.entries()) {
      const x = index * sliceWidth;
      const y = Math.round((height * value) / (sliceDivisors[index] * 512)) * 2;

      if (peaksTtl[index]) {
        peaksTtl[index]--;
      } else {
        peaks[index] = peaksTtl[index] = 0;
      }

      if (y > peaks[index]) {
        peaks[index] = y;
        peaksTtl[index] = 60;
      }

      canvasContext.fillRect(x, height - y, sliceWidth - 1, height);

      if (peaks[index]) {
        canvasContext.beginPath();
        canvasContext.moveTo(x, height - peaks[index] + 0.5);
        canvasContext.lineTo(x + sliceWidth - 1, height - peaks[index] + 0.5);
        canvasContext.stroke();
        canvasContext.closePath();
      }
    }

    for (let index = 0; index < height; index++) {
      if (index % 2) {
        canvasContext.clearRect(0, height - index, width, 1);
      }
    }
  };

  createEffect(() => {
    fixCanvas(outerSize);
  });

  createEffect(() => {
    if (player.state === "play") {
      analyser?.start();
      return;
    }

    analyser?.stop();
  });

  const peaks: number[] = [];
  const peaksTtl: number[] = [];
  let analyser: AudioAnalyser | undefined;

  onMount(() => {
    onPlayerLoad(() => {
      analyser = new AudioAnalyser(window.jhplayer.audioSource, analyseAudio);
    });
  });

  return (
    <div ref={setOuterRef} class={styles.wrapper} aria-hidden="true">
      <canvas ref={setCanvasRef} class={styles.effect} />
    </div>
  );
}
